import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { FunnelSnapshotResponse } from '@rheora/enrollment-contract';

import { readFunnelSnapshotOnce } from '../services/funnel-session.service';
import { getFunnelSnapshot } from './funnel.fetchers';

/**
 * TanStack hooks for the funnel REHYDRATE. `FunnelHydrationGate` calls this;
 * nothing else should need to.
 *
 * ── THE KEY IS THE INVALIDATION TARGET FOR EVERY FUNNEL WRITE ──────────────
 * `funnelQueryKeys.snapshot()` is imported by the mutation hooks in
 * `esiid.queries`, `serviceability.queries`, `email-verification.queries`,
 * `credit.queries`, `deposit.queries`, `estimate.queries` and `sign.queries`, each of
 * which invalidates it on success. That is what keeps a re-render honest: after a
 * meter is resolved or a deposit is settled, the cached snapshot is stale by
 * definition, and a stale snapshot is how a customer ends up looking at a page the
 * server no longer agrees they are on.
 *
 * ⚠️ NOTHING SENSITIVE IS EVER IN THIS KEY. It is a constant tuple — no candidate
 * id, no email, and above all no SSN. That is not an accident of this key being
 * parameterless: the credit check and the OTP verify are MUTATIONS precisely so
 * their variables (the SSN; the one-time code) never become part of a cached query
 * key. See `credit.queries.ts` / `email-verification.queries.ts`.
 */
export const funnelQueryKeys = {
  snapshot: () => ['shop', 'funnel', 'snapshot'] as const,
} as const;

/**
 * The funnel snapshot, fetched once on storefront mount.
 *
 * ── WHY `retry: false` ─────────────────────────────────────────────────────
 * A `401` (no funnel session) is a LEGITIMATE ANSWER — a first-time visitor, or one
 * whose 24h session expired — not a transient failure. Retrying it would delay the
 * first paint of the landing page for every new visitor to re-ask a question already
 * answered. Errors are handled by rendering the empty funnel, so a genuine network
 * blip degrades to "start from the beginning" rather than a spinner that never ends.
 *
 * ── WHY `staleTime: Infinity` + `gcTime: 0` ────────────────────────────────
 * The snapshot is a MOUNT-TIME hydration read, not a live view: within one tab the
 * SPA's own state is authoritative after hydration, and every funnel write
 * invalidates this key explicitly. `staleTime: Infinity` stops a window refocus or a
 * remount re-fetching it for no reason; `gcTime: 0` means the (mildly PII-bearing —
 * it carries the verified email address) payload is dropped from the cache the moment
 * nothing is observing it, rather than lingering for the tab's lifetime.
 *
 * ── WHY THE queryFn IS WRAPPED (THE 767-REQUEST STORM) ─────────────────────
 * ⚠️ `retry: false` IS NOT ENOUGH ON ITS OWN, and believing it was is what let a
 * stale cookie fire 767 `GET /bff/funnel` requests in 14.4 s (QA 2026-08-05). The
 * two options directly above are the amplifier: with no data the query is stale
 * forever, and `gcTime: 0` destroys it the moment nothing observes it — so EVERY
 * fresh observer mount issues a new request, and a session-less funnel step churns
 * observers by redirecting to the landing chooser. `readFunnelSnapshotOnce` makes a
 * 401/403 terminal for that session, so the cost is one request per dead session
 * instead of one per remount. See `funnel-session.service.ts`.
 */
export function useFunnelSnapshotQuery(
  options: { readonly enabled?: boolean } = {},
): UseQueryResult<FunnelSnapshotResponse> {
  return useQuery({
    queryKey: funnelQueryKeys.snapshot(),
    queryFn: () => readFunnelSnapshotOnce(getFunnelSnapshot),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
    // Defaults to enabled, so every existing caller is unchanged. The gate
    // passes `false` on an ordinary page load: reading the snapshot there would
    // restore the previous visit's work, which is exactly what the 2026-09-16
    // decision removed. It asks only on an offer arrival, where the snapshot is
    // this navigation's own server-seeded meter rather than a memory.
    enabled: options.enabled !== false,
  });
}
